// frontend/src/pages/Transport.js
import React, { useEffect, useState } from 'react';
import { Truck, ExternalLink, Loader2, LogIn } from 'lucide-react';
import toast from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { useTransportPortalAccess } from '../hooks/useTransportPortalAccess';
import { authAPI } from '../services/api';

/**
 * Transport launch interstitial.
 *
 * Fetches a one-time launch code and performs a DIRECT top-level navigation to
 * the Transport app's in-scope URL. The direct navigation (no `about:blank`
 * popup) is what lets Chromium hand off to the INSTALLED Transport PWA instead
 * of a browser tab — provided the Transport manifest declares
 * `"handle_links": "preferred"` and `/land` sits inside its `scope`.
 *
 * When auto-redirect doesn't hand off (e.g. code still resolving, or the user
 * navigates back), a real anchor is rendered: a genuine user-gesture link tap
 * is the most reliably captured navigation on Android. On iOS there is no
 * cross-PWA capture, so it opens in an in-app browser view (platform limit).
 */
const Transport = () => {
  const { user } = useAuth();
  const {
    data: transportPortalAccess,
    isLoading: accessLoading,
  } = useTransportPortalAccess({ enabled: !!user });
  const [launching, setLaunching] = useState(false);
  const [transportUrl, setTransportUrl] = useState(null);
  const hasTransportAccess =
    transportPortalAccess?.has_transport_access === true;

  useEffect(() => {
    if (!user || !hasTransportAccess) return;

    let cancelled = false;
    const launch = async () => {
      setLaunching(true);
      try {
        const response = await authAPI.getLaunchCode('transport');
        if (cancelled) return;
        const { url } = response.data;
        setTransportUrl(url);
        window.location.href = url;
      } catch (err) {
        if (cancelled) return;
        setLaunching(false);
        const message =
          err.response?.data?.error ||
          'Unable to open Transport. Please try again from the sidebar.';
        toast.error(message);
      }
    };

    launch();
    return () => { cancelled = true; };
  }, [user, hasTransportAccess]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <LogIn className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Login Required
        </h2>
        <p className="text-gray-500 max-w-md">
          Please log in to the HR Portal and open Transport from the sidebar menu.
        </p>
      </div>
    );
  }

  if (accessLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Checking Transport Access
        </h2>
        <p className="text-gray-500 max-w-md">
          Confirming your Transport Portal access...
        </p>
      </div>
    );
  }

  if (!hasTransportAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <Truck className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Transport Access Unavailable
        </h2>
        <p className="text-gray-500 max-w-md">
          Transport is not available for your account. Contact Transport
          administration if you believe you should have access.
        </p>
      </div>
    );
  }

  if (launching) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Opening Transport
        </h2>
        <p className="text-gray-500 max-w-md">
          Taking you to the Transport app...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
        <Truck className="w-8 h-8 text-blue-600" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Transport
      </h2>
      <p className="text-gray-500 max-w-md mb-6">
        If the Transport app didn't open automatically, tap the button below.
      </p>
      {transportUrl ? (
        <a href={transportUrl} className="btn btn-primary">
          <ExternalLink className="w-4 h-4" />
          Open Transport
        </a>
      ) : (
        <button
          type="button"
          onClick={async () => {
            setLaunching(true);
            try {
              const response = await authAPI.getLaunchCode('transport');
              const { url } = response.data;
              setTransportUrl(url);
              window.location.href = url;
            } catch (err) {
              setLaunching(false);
              const message =
                err.response?.data?.error ||
                'Unable to open Transport. Please try again.';
              toast.error(message);
            }
          }}
          className="btn btn-primary"
        >
          <ExternalLink className="w-4 h-4" />
          Open Transport
        </button>
      )}
    </div>
  );
};

export default Transport;
